import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { useOnSiteStructureChanged } from "../../../hooks/useOnSiteStructureChanged";
import urls from "../../../urls";
import { COMPONENT_QUERY_KEY } from "../../../utils/constants";
import { ObjectName } from "../../../utils/enums";
import type {
  MeterCreateRequest,
  MeterResponse,
  MeterUpdateRequest
} from "../Meter.types";
import { meterQueries } from "./useMeter";

export function useMeterMutation(siteId: number) {
  const queryClient = useQueryClient();
  const cacheName = COMPONENT_QUERY_KEY[ObjectName.Meter];
  const onSiteStructureChanged = useOnSiteStructureChanged(siteId);

  const createMutation = useMutation({
    mutationFn: (createRequest: MeterCreateRequest) =>
      createMeter(createRequest, siteId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      onSiteStructureChanged();
    }
  });

  const editMutation = useMutation({
    mutationFn: (updatedData: MeterUpdateRequest) => editMeter(updatedData),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: meterQueries.detail(response.data.id).queryKey
      });
      onSiteStructureChanged();
    }
  });

  return { createMutation, editMutation };
}

function createMeter(data: MeterCreateRequest, siteId: number) {
  const url = urls.apiWithoutCamelization.meters(siteId);
  return apiWithoutCamelization.post<MeterResponse>(url, data);
}

function editMeter(data: MeterUpdateRequest) {
  const url = urls.apiWithoutCamelization.meter(data.id);
  return apiWithoutCamelization.patch<MeterResponse>(url, data);
}
